<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { Alert, Button, Divider, Heading, LayoutGap, Loader, Prose, Radio } from '@dabble/quillui';
import { addMonths, format } from 'date-fns';
import { computed, ref } from 'vue';

const { t } = useI18n();
const { showToast } = useToast();

const props = defineProps<{ fromCancelSurvey?: boolean }>();

const emit = defineEmits<{
  (e: 'pauseSubscription', payload: { resumeDate: number; length: number }): void;
  (e: 'backToBilling'): void;
  (e: 'cancel'): void;
}>();

const billingStore = useBillingStore();

const isDelinquent = computed(() => billingStore.isDelinquent);

const periodStartDate = computed(() => {
  const ts = billingStore.primarySubscription?.current_period_start;
  return ts ? new Date(ts * 1000) : null;
});

const formattedPeriodStart = computed(() => (periodStartDate.value ? format(periodStartDate.value, 'PP') : ''));

type Step = 'duration' | 'success';
const step = ref<Step>('duration');

const selectedOption = ref<string>(props.fromCancelSurvey ? 'cancel' : '');
const saving = ref(false);
const failed = ref(false);
const pauseOptions = [1, 2, 3];
const successPayload = ref<{ resumeDate: number; length: number } | null>(null);

const selectedLength = computed(() => {
  const length = parseInt(selectedOption.value, 10);
  return isNaN(length) || !pauseOptions.includes(length) ? null : length;
});

const pauseAnchor = computed(() => {
  if (!isDelinquent.value || !periodStartDate.value) return new Date();
  // If the user's been past-due longer than their chosen pause duration, anchoring at period_start would put
  // resumes_at in the past — Stripe would auto-expire the pause immediately. Fall back to "now" so they
  // actually get the pause length they picked.
  const length = selectedLength.value;
  if (length && addMonths(periodStartDate.value, length).getTime() <= Date.now()) return new Date();
  return periodStartDate.value;
});

const resumeDate = computed(() => {
  const length = selectedLength.value;
  return length ? addMonths(pauseAnchor.value, length) : null;
});

const formattedResumeDate = computed(() => (resumeDate.value ? format(resumeDate.value, 'PP') : ''));

function onConfirmDuration() {
  if (!selectedLength.value) return;
  void doPause();
}

function onSuccessClose() {
  if (successPayload.value) emit('pauseSubscription', successPayload.value);
}

async function doPause() {
  const length = selectedLength.value;
  if (!length || !resumeDate.value) return;

  saving.value = true;
  failed.value = false;
  const resumeTimestamp = Math.floor(resumeDate.value.getTime() / 1000);

  try {
    const ok = await billingStore.pauseBillingSubscription(resumeTimestamp);
    if (!ok) return;

    successPayload.value = { resumeDate: resumeDate.value.getTime(), length };
    step.value = 'success';
  } catch (e: any) {
    logError('Failed to pause subscription:', e);
    failed.value = true;
    showToast('danger', e instanceof Error ? e.message : t('pause_subscription_failed'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <LayoutGap v-if="step === 'success'" direction="column" size="medium">
    <Heading :level="3" no-margin>{{ t('pause_subscription_success_title') }}</Heading>
    <Prose>{{ t('pause_subscription_success_body', { date: formattedResumeDate }) }}</Prose>
    <LayoutGap direction="row" align="right" size="small">
      <Button variant="primary" @click="onSuccessClose">
        {{ t('pause_subscription_close') }}
      </Button>
    </LayoutGap>
  </LayoutGap>

  <LayoutGap v-else direction="column" size="medium">
    <Alert
      v-if="isDelinquent && formattedPeriodStart"
      variant="warning"
      :title="t('pause_subscription_warning_past_due_title')"
      :message="
        t('pause_subscription_warning_past_due_body', {
          startDate: formattedPeriodStart,
          resumeDate: formattedResumeDate || t('pause_subscription_pick_duration'),
        })
      "
    />
    <LayoutGap direction="column" size="xsmall">
      <Radio
        v-for="option in pauseOptions"
        :key="option"
        v-model="selectedOption"
        :value="option.toString()"
        :label="t(`pause_subscription_option_${option}`)"
        name="pauseLength"
      />

      <template v-if="fromCancelSurvey">
        <Divider />
        <Radio
          v-model="selectedOption"
          value="cancel"
          :label="t('pause_subscription_cancel_instead')"
          name="pauseLength"
        />
      </template>
    </LayoutGap>

    <LayoutGap direction="row" align="right" vertical-align="center" wrap size="small">
      <Button variant="secondary" finish="outlined" :disabled="saving" @click="emit('backToBilling')">
        {{ t('exit_survey_never_mind') }}
      </Button>

      <LayoutGap v-if="saving" direction="row" vertical-align="center" size="small">
        <Loader />
        <Prose variant="secondary">{{ t('exit_survey_processing') }}</Prose>
      </LayoutGap>
      <template v-else-if="selectedOption === 'cancel' || (fromCancelSurvey && !selectedOption)">
        <Button variant="danger" @click="emit('cancel')">
          {{ t('pause_subscription_confirm_cancellation') }}
        </Button>
      </template>
      <Button v-else variant="primary" :disabled="!selectedOption" @click="onConfirmDuration">
        {{ t('pause_subscription_confirm') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
</template>
